import { Box, Stack } from "@mui/material";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { calcProPercentsBasedOnFilterAndDays, itemPercentage, percentageLabel } from "../../../../utilis/common";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../../../utilis/percentage-comparison";
import CardItemTooltip from "../../../../components/shared/CardItemTooltip";
import ColorBox from "../../../../components/shared/dashboard/ColorBox";
import CheckboxButton from "../../../../components/dashboard/shared/checkboxButton/CheckboxButton";
import CheckboxCircle from "../../../../components/dashboard/shared/checkboxButton/CheckboxCircle";
import styles from "./ShiftCard.module.scss";
import ShiftCardSkeleton from "./ShiftCardSkeleton";
import useLoadingToggle from "../../../../components/hooks/useLoadingToggle";
import CheckboxLoader from "../../../../components/shared/checkbox-loader/CheckboxLoader";

// Constants
const PERCENTAGE_THRESHOLD = 20;
const BAR_COLORS = {
	SELECTED: "#4879f5",
	DEFAULT: "#636578"
};
const COLOR_BOX_DIMENSIONS = { height: "25px", width: "5px", mr: 0.1 };

const TotalTextWithBar = ({
	total,
	percentage,
	comparisonColor,
	comparingAgainst,
	comparingAgainstScaled,
	numberOfDays,
	loading,
	isPercentageVisible = true,
}) => {
	return (
		<Stack direction="row" display="flex" alignItems="center">
			{isPercentageVisible && (
				<Stack>
					{!loading && (
						<ColorBox
							color={comparisonColor}
							sx={COLOR_BOX_DIMENSIONS}
							comparingAgainst={comparingAgainst}
							comparingAgainstScaled={comparingAgainstScaled}
							numberOfDays={numberOfDays}
						/>
					)}
				</Stack>
			)}
			<Stack justifyContent="center" display="flex" alignItems="center">
				<p className={`fs15 ffmar-bold ${styles.totalText}`}>{total || 0}</p>
				{isPercentageVisible && (
					<p className={`fs12 ffmar-reg ${styles.percentageText}`}>{percentageLabel(percentage)}</p>
				)}
			</Stack>
		</Stack>
	);
};

const ShiftCard = ({
	data,
	dataComparison,
	handleToggle,
	selectedItem = [],
	type,
	page,
	averageCensusComparison,
	averageCensus,
	filter,
	filterComparison,
	cardTitle,
	loading,
	projectionDays,
	priorityNumber,
	transferType,
	isComparingAgainstAvgCensus,
	lockedTotalBy,
	isCustom = false,
	question, 
	isCustomTab = false,
	customTab = null,
	disableInteractions,
}) => {
	const { loadingItems, handleToggleWithLoader } = useLoadingToggle();
	// Computed values
	const isFirstItemInPriorityData = priorityNumber === 1;
	const shouldUseCensusCalculation = useMemo(() => 
		isComparingAgainstAvgCensus ||
		lockedTotalBy === "census" ||
		(isFirstItemInPriorityData && (!transferType || transferType.length === 0)),
		[isComparingAgainstAvgCensus, lockedTotalBy, isFirstItemInPriorityData, transferType]
	);

	// Helper functions
	const getItemComparison = useCallback((itemId) => 
		_.find(dataComparison, { _id: itemId }), 
		[dataComparison]
	);

	const isItemSelected = useCallback((itemId) => 
		selectedItem.indexOf(itemId) !== -1, 
		[selectedItem]
	);

	const calculateItemData = useCallback((item, itemComparison) => {
		const percentageOfAverageCensus = itemPercentage(item.value, averageCensus, "number");
		const percentageOfAverageCensusComparison = itemPercentage(
			itemComparison?.value || 0,
			averageCensusComparison,
			"number"
		);

		const { percentageDiff, itemTotalComparisonScaled, numberOfDays } = calculateDiffBetweenPercentages(
			percentageOfAverageCensus,
			percentageOfAverageCensusComparison,
			false,
			filter,
			filterComparison,
			itemComparison?.value,
			projectionDays
		);

		const comparisonColor = pickComparisonColor(percentageDiff);
		const value = calcProPercentsBasedOnFilterAndDays(item.value, filter, projectionDays);
		const percentage = calcProPercentsBasedOnFilterAndDays(
			item.percentage,
			filter,
			projectionDays,
			true,
			shouldUseCensusCalculation
		);

		return {
			comparisonColor,
			value,
			percentage,
			itemTotalComparisonScaled,
			numberOfDays
		};
	}, [averageCensus, averageCensusComparison, filter, filterComparison, projectionDays, shouldUseCensusCalculation]);

	const handleItemToggle = useCallback((item) => {
		if (disableInteractions) return;
		
		const isChecked = isItemSelected(item._id);
		handleToggleWithLoader(() => handleToggle({
			item,
			type,
			isChecked,
			cardTitle,
			question: isCustom
				? { 
					isCustom: true, 
					customQuestionInputType: question?.customQuestionInputType, 
					isCustomTab, 
					cardTitle, 
					customTab 
				}
				: null
		}), item._id);
	}, [disableInteractions, handleToggleWithLoader, handleToggle, type, cardTitle, isCustom, question, isCustomTab, customTab, isItemSelected]);

	// Memoized values
	const hasData = useMemo(() => data && data.length > 0, [data]);
	const isDcSelected = useMemo(() => selectedItem.indexOf("DC") !== -1, [selectedItem]);
	const itemCount = useMemo(() => data?.length || 0, [data]);

	// Calculate max percentage for relative bar scaling
	const maxPercentage = useMemo(() => {
		if (!data || data.length === 0) return 100;
		const percentages = data.map(item => item.percentage || 0);
		const max = Math.max(...percentages);
		// Return at least 10 to ensure some bars are visible, or the actual max if higher
		return max > 0 ? max : 100;
	}, [data]);
	
	// Calculate dynamic spacing for bars
	const barSpacing = useMemo(() => {
		if (itemCount === 0) return { width: "25%", margin: "0px" };
		
		// If 5 or more items (after 4), use fixed width for horizontal scroll
		if (itemCount >= 5) {
			return {
				width: "140px",
				margin: "0px",
				minWidth: "140px"
			};
		}
		
		// For 4 or fewer items, use flex with gap for better spacing
		// Calculate appropriate width based on item count
		if (itemCount === 1) {
			return { width: "100%", margin: "0px" };
		} else if (itemCount === 2) {
			return { width: "calc(50% - 12px)", margin: "0px" };
		} else if (itemCount === 3) {
			return { width: "calc(33.333% - 16px)", margin: "0px" };
		} else if (itemCount === 4) {
			return { width: "calc(25% - 18px)", margin: "0px" };
		}
		
		return { width: "25%", margin: "0px" };
	}, [itemCount]);
	
	const shouldScroll = useMemo(() => itemCount >= 5, [itemCount]);

	const renderItemColumn = useCallback((item, index) => {
		const ID = item._id;
		const itemComparison = getItemComparison(ID);
		const selected = isItemSelected(ID);
		const itemData = calculateItemData(item, itemComparison);
		const { comparisonColor, value, percentage, itemTotalComparisonScaled, numberOfDays } = itemData;
		const isSpacialItem = item?.isSpacialItem ?? false;
		const isPercentageVisible = item?.isPercentageVisible ?? true;

		const barBackgroundColor = selected ? BAR_COLORS.SELECTED : BAR_COLORS.DEFAULT;
		const shouldShowTextAbove = percentage <= PERCENTAGE_THRESHOLD;
		const shouldShowTextInside = percentage > PERCENTAGE_THRESHOLD;
		const labelId = `checkbox-list-return-label-${ID}`;

		return (
			<div
				key={ID}
				className={`df ffc ${styles.itemColumn} ${shouldScroll ? styles.scrollableColumn : ""}`}
				style={{
					width: barSpacing.width,
					...(barSpacing.minWidth && { minWidth: barSpacing.minWidth })
				}}
			>				
				<div
					className={`df ffc aic ${styles.barWrpr} ${isDcSelected ? styles.selectedItem : ""}`}
					id={`DC${index}`}
				>
					{shouldShowTextAbove && (
						<TotalTextWithBar
							total={value}
							percentage={percentage}
							comparisonColor={comparisonColor}
							comparingAgainst={itemComparison?.value}
							comparingAgainstScaled={itemTotalComparisonScaled}
							numberOfDays={numberOfDays}
							loading={loading}
							isPercentageVisible={isPercentageVisible}
						/>
					)}
					<div
						className={`df ffc acc ${styles.bar}`}
						style={{
							height: `${Math.max((item.percentage / maxPercentage) * 100, 2)}%`,
							background: barBackgroundColor,
						}}
					>
						{shouldShowTextInside && (
							<TotalTextWithBar
								total={value}
								percentage={percentage}
								comparisonColor={comparisonColor}
								comparingAgainst={itemComparison?.value}
								comparingAgainstScaled={itemTotalComparisonScaled}
								numberOfDays={numberOfDays}
								loading={loading}
								isPercentageVisible={isPercentageVisible}
							/>
						)}
					</div>
				</div>

				{/* Checkbox Section */}
				<CardItemTooltip item={item}>
					<div
						className={`df cp aic ${styles.sec} ${selected ? styles.selected : ""}`}
						{...(isSpacialItem ? {} : { onClick: () => handleItemToggle(item) })}
					>
						<Box position="relative" display="inline-flex" alignItems="center">
							{isSpacialItem ? (
								<CheckboxCircle
									labelId={labelId}
									item={item}
									buttonText={item.label}
									handleToggle={() => {
										if (disableInteractions) return;
										handleToggleWithLoader(() => handleToggle({
											item,
											type,
											itemComparison: itemComparison,
											isChecked: selected,
											cardTitle,
											isSpacialItem: isSpacialItem,
											isCustomTab,
											customTab
										}), ID);
									}}
									sx={{
										...(loadingItems[ID] && { opacity: 0 })
									}}
									checked={selected}
									disabled={disableInteractions}
								/>
							) : (
								<CheckboxButton
									className={styles.shiftCheckbox}
									labelId={labelId}
									checked={selected}
									buttonText={item.label}
									sx={{
										...(loadingItems[item._id] && { opacity: 0 })
									}}
									disabled={disableInteractions}
								/>
							)}
							<CheckboxLoader
								className="small-dots-day-list"
								isLoading={loadingItems[item._id]}
							/>
						</Box>
					</div>
				</CardItemTooltip>
			</div>
		);
	}, [getItemComparison, isItemSelected, calculateItemData, isDcSelected, loading, barSpacing, shouldScroll, handleItemToggle, loadingItems, disableInteractions, maxPercentage, handleToggleWithLoader, handleToggle, type, cardTitle, isCustomTab, customTab]);


	if (loading) {
		return <ShiftCardSkeleton />;
	}

	return (
		<div className={`w100 ${styles.shiftsSec} ${shouldScroll ? styles.scrollableContainer : ""}`}>
			<div className={`ffmar df jcc ${styles.itemsContainer} ${shouldScroll ? styles.scrollableContent : ""}`}>
				{hasData && data.map(renderItemColumn)}
			</div>
		</div>
	);
};

export default ShiftCard;
